// Code-Analyse-Utilities

// API-Endpunkte extrahieren (Anwendungsschnittstellen)
export const extractAPIEndpoints = (content, filename) => {
  const endpoints = [];
  
  // Express.js Routes
  const expressRoutes = content.match(/(?:app|router)\.(get|post|put|delete|patch)\s*\(\s*['"`]([^'"`]+)['"`]/g);
  if (expressRoutes) {
    expressRoutes.forEach(match => {
      const [_, method, path] = match.match(/(?:app|router)\.(get|post|put|delete|patch)\s*\(\s*['"`]([^'"`]+)['"`]/);
      endpoints.push({ framework: 'Express', method: method.toUpperCase(), path, file: filename });
    });
  }
  
  // Flask Routes
  const flaskRoutes = content.match(/@app\.route\s*\(\s*['"`]([^'"`]+)['"`]/g);
  if (flaskRoutes) {
    flaskRoutes.forEach(match => {
      const path = match.match(/@app\.route\s*\(\s*['"`]([^'"`]+)['"`]/)[1];
      endpoints.push({ framework: 'Flask', method: 'GET', path, file: filename });
    });
  }
  
  // FastAPI Routes
  const fastapiRoutes = content.match(/@(?:app|router)\.(get|post|put|delete|patch)\s*\(\s*['"`]([^'"`]+)['"`]/g);
  if (fastapiRoutes) {
    fastapiRoutes.forEach(match => {
      const [_, method, path] = match.match(/@(?:app|router)\.(get|post|put|delete|patch)\s*\(\s*['"`]([^'"`]+)['"`]/);
      endpoints.push({ framework: 'FastAPI', method: method.toUpperCase(), path, file: filename });
    });
  }
  
  return endpoints;
};

// Symbole (Klassen und Funktionen) extrahieren
export const extractSymbols = (content) => {
  const symbols = { classes: [], functions: [], exports: [] };
  
  // Classes
  const classMatches = content.match(/class\s+(\w+)(?:\s+extends\s+\w+)?\s*{/g);
  if (classMatches) {
    classMatches.forEach(match => {
      const className = match.match(/class\s+(\w+)/)[1];
      symbols.classes.push(className);
    });
  }
  
  // Functions
  const functionMatches = content.match(/(?:function\s+(\w+)|const\s+(\w+)\s*=\s*(?:async\s+)?\(|export\s+(?:async\s+)?function\s+(\w+))/g);
  if (functionMatches) {
    functionMatches.forEach(match => {
      const funcName = match.match(/(?:function\s+(\w+)|const\s+(\w+)\s*=\s*(?:async\s+)?\(|export\s+(?:async\s+)?function\s+(\w+))/)[1] || 
                     match.match(/(?:function\s+(\w+)|const\s+(\w+)\s*=\s*(?:async\s+)?\(|export\s+(?:async\s+)?function\s+(\w+))/)[2] ||
                     match.match(/(?:function\s+(\w+)|const\s+(\w+)\s*=\s*(?:async\s+)?\(|export\s+(?:async\s+)?function\s+(\w+))/)[3];
      if (funcName && funcName !== 'if' && funcName !== 'for' && funcName !== 'while') {
        symbols.functions.push(funcName);
      }
    });
  }
  
  // Exports
  const exportMatches = content.match(/export\s+(?:default\s+)?(?:const|let|var|function|class)\s+(\w+)/g);
  if (exportMatches) {
    exportMatches.forEach(match => {
      const exportName = match.match(/export\s+(?:default\s+)?(?:const|let|var|function|class)\s+(\w+)/)[1];
      symbols.exports.push(exportName);
    });
  }
  
  return symbols;
};

// Import-Statements extrahieren für Dependency-Crawler (Abhängigkeits-Sucher)
export const extractImports = (content) => {
  const imports = { local: [], external: [] };
  
  // ES6 Imports
  const es6Imports = content.match(/import\s+(?:\{[^}]*\}|\w+|\*\s+as\s+\w+)\s+from\s+['"`]([^'"`]+)['"`]/g);
  if (es6Imports) {
    es6Imports.forEach(match => {
      const path = match.match(/from\s+['"`]([^'"`]+)['"`]/)[1];
      if (path.startsWith('.') || path.startsWith('/')) {
        imports.local.push(path);
      } else {
        imports.external.push(path);
      }
    });
  }
  
  // CommonJS Requires
  const requires = content.match(/require\s*\(\s*['"`]([^'"`]+)['"`]\s*\)/g);
  if (requires) {
    requires.forEach(match => {
      const path = match.match(/require\s*\(\s*['"`]([^'"`]+)['"`]\s*\)/)[1];
      if (path.startsWith('.') || path.startsWith('/')) {
        imports.local.push(path);
      } else {
        imports.external.push(path);
      }
    });
  }
  
  return imports;
};

// Tech-Stack aus package.json oder requirements.txt erkennen
export const detectTechStack = (files) => {
  const techStack = { frameworks: [], languages: [], databases: [], tools: [] };
  
  const packageJson = files.find(f => f.path.endsWith('package.json'));
  if (packageJson && packageJson.content) {
    try {
      const pkg = JSON.parse(packageJson.content);
      const deps = { ...pkg.dependencies, ...pkg.devDependencies };
      
      if (deps.react) techStack.frameworks.push('React');
      if (deps.vue) techStack.frameworks.push('Vue');
      if (deps.angular) techStack.frameworks.push('Angular');
      if (deps['@angular/core']) techStack.frameworks.push('Angular');
      if (deps.next) techStack.frameworks.push('Next.js');
      if (deps.nuxt) techStack.frameworks.push('Nuxt.js');
      if (deps.express) techStack.frameworks.push('Express.js');
      if (deps['@supabase/supabase-js']) techStack.databases.push('Supabase');
      if (deps.mongodb) techStack.databases.push('MongoDB');
      if (deps.mongoose) techStack.databases.push('Mongoose');
      if (deps.pg) techStack.databases.push('PostgreSQL');
      if (deps.mysql) techStack.databases.push('MySQL');
      if (deps.sqlite3) techStack.databases.push('SQLite');
      if (deps.tailwindcss) techStack.tools.push('Tailwind CSS');
      if (deps.vite) techStack.tools.push('Vite');
      if (deps.webpack) techStack.tools.push('Webpack');
    } catch (e) {
      console.error('Fehler beim Parsen von package.json:', e);
    }
  }
  
  const requirementsTxt = files.find(f => f.path.endsWith('requirements.txt'));
  if (requirementsTxt && requirementsTxt.content) {
    const reqs = requirementsTxt.content.toLowerCase();
    if (reqs.includes('django')) techStack.frameworks.push('Django');
    if (reqs.includes('flask')) techStack.frameworks.push('Flask');
    if (reqs.includes('fastapi')) techStack.frameworks.push('FastAPI');
    if (reqs.includes('sqlalchemy')) techStack.databases.push('SQLAlchemy');
    if (reqs.includes('pymongo')) techStack.databases.push('MongoDB');
  }
  
  return techStack;
};

// Datenbank-Schema aus SQL-Dateien extrahieren
export const extractDatabaseSchema = (files) => {
  const schemas = [];
  
  files.forEach(file => {
    if (file.path.endsWith('.sql') && file.content) {
      const tables = [];
      
      // CREATE TABLE Statements
      const tableMatches = file.content.match(/CREATE\s+TABLE\s+(?:IF\s+NOT\s+EXISTS\s+)?(\w+)/gi);
      if (tableMatches) {
        tableMatches.forEach(match => {
          const tableName = match.match(/CREATE\s+TABLE\s+(?:IF\s+NOT\s+EXISTS\s+)?(\w+)/i)[1];
          tables.push(tableName);
        });
      }
      
      if (tables.length > 0) {
        schemas.push({
          file: file.path,
          tables: tables
        });
      }
    }
  });
  
  return schemas;
};

// Smart-Squeeze: Kommentare und Leerraum entfernen
export const smartSqueeze = (content) => {
  let squeezed = content;
  
  // Einzeilige Kommentare entfernen (//)
  squeezed = squeezed.replace(/\/\/.*$/gm, '');
  
  // Mehrzeilige Kommentare entfernen (/* */)
  squeezed = squeezed.replace(/\/\*[\s\S]*?\*\//g, '');
  
  // Python-Kommentare entfernen (#)
  squeezed = squeezed.replace(/#.*$/gm, '');
  
  // Mehrfache Leerzeilen reduzieren
  squeezed = squeezed.replace(/\n{3,}/g, '\n\n');
  
  // Führende und nachfolgende Leerzeichen in jeder Zeile entfernen
  squeezed = squeezed.split('\n').map(line => line.trim()).join('\n');
  
  return squeezed;
};

// PII-Scrubber: Persönliche Daten entfernen
export const scrubPII = (content) => {
  let scrubbed = content;
  
  // E-Mail-Adressen
  scrubbed = scrubbed.replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '[EMAIL_REDACTED]');
  
  // IP-Adressen
  scrubbed = scrubbed.replace(/\b(?:\d{1,3}\.){3}\d{1,3}\b/g, '[IP_ADDRESS_REDACTED]');
  
  // API-Keys (heuristic)
  scrubbed = scrubbed.replace(/['"`]?(?:api|secret|token|key)['"`?\s*[:=]\s*['"`][A-Za-z0-9]{20,}['"`]/gi, '[API_KEY_REDACTED]');
  
  // URLs mit Authentifizierung
  scrubbed = scrubbed.replace(/https?:\/\/[^:\s]+:[^@\s]+@/g, 'https://[AUTH_REDACTED]@');
  
  return scrubbed;
};

// ASCII-Baumstruktur generieren
export const generateAsciiTree = (items) => {
  const tree = [];
  const maxDepth = Math.max(...items.map(item => item.path.split('/').length));
  
  items.forEach((item, index) => {
    const parts = item.path.split('/');
    const depth = parts.length - 1;
    const isLast = index === items.length - 1;
    
    let prefix = '';
    for (let i = 0; i < depth; i++) {
      const hasSiblingAtDepth = items.some(other => {
        const otherParts = other.path.split('/');
        return otherParts.length > i + 1 && otherParts.slice(0, i + 1).join('/') === parts.slice(0, i + 1).join('/') && other.path > item.path;
      });
      prefix += hasSiblingAtDepth ? '│   ' : '    ';
    }
    
    prefix += isLast ? '└── ' : '├── ';
    const icon = item.type === 'folder' ? '📁' : '📄';
    tree.push(prefix + icon + ' ' + parts[parts.length - 1]);
  });
  
  return tree.join('\n');
};